<script>
import * as d3 from 'd3';
import _ from 'lodash';
import { Container, ResizeObserver, XAxis, YAxis, createTooltip, wrapText } from '../chart-services';

export default {
  name: 'LollipopChart',
  props: {
    width: { type: String, default: '960' }, // 'number' | 'auto'
    height: { type: Number, default: 480 },
    margin: { type: Object },
    xAxis: { type: Object },
    xScale: { type: Object },
    yAxis: { type: Object },
    yScale: { type: Object },
    data: { type: Array },
    dataPoint: { type: Object },
    transitionDuration: { type: Number, default: 1000 },
    selectedLegend: { type: String },
    hoveringLegend: { type: String },
    joinPoints: { type: Boolean, default: true }
  },
  data: function () {
    return {
      containerInfo: null,
      tooltip: null,
      noData: true,
      chartData: undefined,
      className: {
        wrapperDataPoint: 'wrapper-data-point'
      },
      computedValue: {
        margin: undefined,
        xAxis: undefined,
        xScale: undefined,
        yAxis: undefined,
        yScale: undefined,
        dataPoint: undefined,
        width: this.width,
        height: this.height
      },
      valFormat: d3.format('.1f'),
      resizeObserver: null
    };
  },
  watch: {
    dataPoint: function (newVal, oldVal) {
      const newDataPointKeys = Object.keys(newVal);
      const oldDataPointKeys = Object.keys(oldVal);
      if (newDataPointKeys.join() !== oldDataPointKeys.join()) {
        // Non relevance data point(s) didn't reuse on re-rendering process,
        // we need to clear it
        this.containerInfo.$chartWrapper.selectAll(`.${this.className.wrapperDataPoint}`).remove();
      }
    },
    data: function () {
      if (this.checkNoData() === true) return;
      this.setComputedValue();
      this.setChartData();
      this.render();
    },
    hoveringLegend: function () {
      this.render();
    },
    selectedLegend: function () {
      this.render();
    }
  },
  mounted: function () {
    this.init();
    if (this.width === 'auto') {
      this.resizeObserver = ResizeObserver.observe(this.$el, () => {
        this.$refs.chartContainer.innerHTML = '';
        this.init();
      }, { onlyWidth: true, skipFirst: true });
    }
  },
  destroyed: function () {
    if (this.resizeObserver !== null) {
      ResizeObserver.unobserve(this.resizeObserver);
    }
  },
  methods: {
    init: function () {
      if (this.checkNoData() === true) return;
      this.setChartData();
      this.setComputedValue();
      this.renderContainer();
      this.render();
    },
    checkNoData: function () {
      if (this.data === undefined) {
        this.noData = true;
        this.$refs.chartContainer.innerHTML = null;
        this.$refs.chartContainer.style.width = null;
        this.$refs.chartContainer.style.height = null;
        return true;
      }
      this.noData = false;
      return false;
    },
    setChartData: function () {
      this.chartData = JSON.parse(this.data).data;
    },
    setComputedValue: function () {
      const defaultMargin = {
        top: 10,
        right: 50,
        bottom: 75,
        left: 50
      };
      this.computedValue.margin = { ...defaultMargin, ...this.margin };
      const defaultXAxis = {
        fixed: false,
        grid: false,
        rotate: 45,
        wrap: true,
        label: undefined
      };
      this.computedValue.xAxis = { ...defaultXAxis, ...this.xAxis };
      const defaultXScale = {
        field: 'label',
        domain: undefined
      };
      this.computedValue.xScale = { ...defaultXScale, ...this.xScale };
      const defaultYAxis = {
        grid: false,
        wrap: true,
        label: undefined
      };
      this.computedValue.yAxis = { ...defaultYAxis, ...this.yAxis };
      const defaultYScale = {
        dataPointFields: ['val1', 'val2', 'val3'],
        domain: undefined
      };
      this.computedValue.yScale = { ...defaultYScale, ...this.yScale };
      const defaultDataPointStyle = {};
      this.computedValue.yScale.dataPointFields.forEach(fieldCircle => {
        defaultDataPointStyle[fieldCircle] = { el: 'circle', fill: '#000', stroke: '#fff' };
      });
      this.computedValue.dataPoint = this.dataPoint ? this.dataPoint : defaultDataPointStyle;
      this.computedValue.width = (this.width === 'auto' ? this.$el.offsetWidth : this.width) -
        this.computedValue.margin.left -
        this.computedValue.margin.right;
      this.computedValue.height = this.height;
    },
    renderContainer: function () {
      const containerHeight = this.computedValue.height;

      this.containerInfo = Container.renderContainer(
        this.$refs.chartContainer,
        this.computedValue.width,
        this.height,
        containerHeight,
        this.computedValue.margin, {
          isXAxisFixed: this.computedValue.xAxis.fixed,
          hasXAxisLabel: this.computedValue.xAxis.label !== undefined
        },
        { transitionDuration: this.transitionDuration }
      );
      this.tooltip = createTooltip();
      this.containerInfo.$chartWrapper.call(this.tooltip.tip);
    },
    render: function () {
      if (this.containerInfo === null || this.tooltip === null) return;

      function getDataPointVals (d, dataPointFields) {
        const dataPointVals = dataPointFields.map(d2 => d[d2]);
        return dataPointVals;
      }

      /* YAxis */
      const customDomain = this.computedValue.yScale.domain;
      const yDomain = [];
      if (customDomain !== undefined) {
        if (customDomain.length > 2) {
          yDomain.push(customDomain[0]);
          yDomain.push(customDomain[customDomain.length - 1]);
        } else {
          yDomain.push(...customDomain);
        }
      } else {
        const dataPointVals = _.flatMap(this.chartData.data, d => {
          return getDataPointVals(d, this.computedValue.yScale.dataPointFields);
        });
        const yMin = d3.min(dataPointVals);
        const yMax = d3.max(dataPointVals);
        yDomain.push(yMin);
        yDomain.push(yMax);
      }
      const yScale = YAxis.getYScale([this.computedValue.height, 0], yDomain, { scaleType: 'scaleLinear' });
      const yAxis = YAxis.getYAxis(yScale, { customDomain: customDomain.length > 2 ? customDomain : undefined });
      const renderYAxisOption = { transitionDuration: this.transitionDuration };
      if (this.computedValue.yAxis.label !== undefined) {
        renderYAxisOption.label = {
          position: { x: -this.computedValue.margin.left },
          text: this.computedValue.yAxis.label
        };
      }
      YAxis.renderYAxis(this.containerInfo.$chartWrapper, yAxis, renderYAxisOption);
      /* /YAxis */

      /* XAxis */
      const xScale = XAxis.getXScale(
        [0, this.computedValue.width],
        this.computedValue.xScale.domain !== undefined
          ? this.computedValue.xScale.domain
          : this.chartData.data.map(d => d[this.computedValue.xScale.field]),
        { scaleType: 'scaleBand' }
      );
      const xAxis = XAxis.getXAxis(xScale);
      const renderXAxisOption = {
        translate: { x: 0, y: this.computedValue.height },
        rotate: this.computedValue.xAxis.rotate,
        transitionDuration: this.transitionDuration
      };
      if (this.computedValue.xAxis.label !== undefined) {
        renderXAxisOption.label = { text: this.computedValue.xAxis.label };
      }
      const $xAxis = XAxis.renderXAxis(this.containerInfo.$chartWrapper, xAxis, renderXAxisOption);
      const centerXAxisTick = xScale.bandwidth() / 2;
      /* /XAxis */

      /* Group Axis */
      if (this.chartData.groupAxisData) {
        const chartGroups = [];
        this.chartData.groupAxisData.forEach(function (d, i, array) {
          chartGroups.push({
            group: d.name,
            length: d.data.length,
            start: xScale(d.start),
            end: xScale(d.end)
          });
        });

        const xAxisHeight = $xAxis.empty() ? 0 : $xAxis.node().getBBox().height;
        const spacing = 5;
        const chartGroupWrapperEnter = this.containerInfo.$chartWrapper
          .selectAll('.wrapper-chart-groups')
          .data([0])
          .enter()
          .append('g')
          .attr('class', 'wrapper-chart-groups')
          .attr('transform', `translate(0, ${(this.computedValue.height + (xAxisHeight ? xAxisHeight + spacing : 0))})`);
        /* chart-groups */
        const chartGroup = chartGroupWrapperEnter
          .selectAll('.chart-groups')
          .data(chartGroups)
          .enter()
          .append('g')
          .attr('class', 'chart-group');
        chartGroup.append('text')
          .attr('x', d => {
            const charWidth = 2;
            const textWidth = (d.group.length * charWidth);
            if (textWidth > d.end) return (d.start + d.end) / 2;
            return (d.start + d.end + textWidth) / 2;
          })
          .attr('y', '1em')
          .attr('text-anchor', 'middle')
          .attr('fill', '#8f8f8f')
          .attr('font-size', '11px')
          .attr('data-width', d => {
            // const temp = (d.end - d.start) + 35;
            const minWidth = 35;
            const width = d.end - d.start;
            return width > minWidth ? width : minWidth;
          })
          .text(d => d.group)
          .call(wrapText, 'data-width');
        /* /chart-groups */
        chartGroup.append('line')
          .style('stroke', '#8f8f8f')
          .style('stroke-width', (d, i) => {
            return i > 0 ? '1px' : '0px';
          })
          .attr('x1', d => {
            return d.start;
          })
          .attr('y1', 0)
          .attr('x2', d => {
            return d.start;
          })
          .attr('y2', 25);

        const chartGroupWrapper = this.containerInfo.$chartWrapper.select('.wrapper-chart-groups');
        const height = this.containerInfo.chartContainerHeight + chartGroupWrapper.node().getBBox().height;
        this.$refs.chartContainer.style.height = `${height}px`;
        this.$refs.chartContainer.querySelector('svg').setAttribute('height', height);
      } /* /groupAxis */

      /* Line */
      if (this.joinPoints) {
        function renderLine (el, xField, dataPointFields, hoveringLegend, selectedLegend) {
          el
            .style('opacity', d => {
              if (hoveringLegend) {
                return 0;
              } else if (selectedLegend) {
                return 0.1;
              } else {
                return 1;
              }
            })
            .attr('x1', d => xScale(d[xField]) + centerXAxisTick)
            .attr('x2', d => xScale(d[xField]) + centerXAxisTick)
            .attr('y1', d => {
              const dVal = d3.min(getDataPointVals(d, dataPointFields));
              return yScale(dVal);
            })
            .attr('y2', d => {
              const dVal = d3.max(getDataPointVals(d, dataPointFields));
              return yScale(dVal);
            });
        }
        const lineWrapper = this.containerInfo.$chartWrapper
          .selectAll('.wrapper-line')
          .data([1])
          .join('g')
          .attr('class', 'wrapper-line');
        lineWrapper
          .selectAll('.line')
          .data(this.chartData.data)
          .join(
            enter => {
              enter
                .append('line')
                .attr('class', 'line')
                .call(el => renderLine(el,
                  this.computedValue.xScale.field,
                  this.computedValue.yScale.dataPointFields,
                  this.hoveringLegend,
                  this.selectedLegend));
            },
            update => {
              update
                .transition()
                .duration(this.transitionDuration)
                .call(el => renderLine(el,
                  this.computedValue.xScale.field,
                  this.computedValue.yScale.dataPointFields,
                  this.hoveringLegend,
                  this.selectedLegend));
            },
            exit => {
              exit.remove();
            }
          );
      }
      /* /Line */

      /* Data Point */
      const triangleSize = 80;
      const triangle = d3.symbol()
        .type(d3.symbolTriangle)
        .size(triangleSize);
      function renderDataPoint (el, dataPoint, xField, yField, hoveringLegend, selectedLegend) {
        const self = this;
        el
          .attr('class', (d) => {
            return 'data-point';
          })
          .style('opacity', d => {
            if (hoveringLegend) {
              if (dataPoint.label !== hoveringLegend) {
                return 0;
              } else {
                return 1;
              }
            } else if (selectedLegend) {
              if (dataPoint.label !== selectedLegend) {
                return 0.1;
              } else {
                return 1;
              }
            } else {
              return 1;
            }
          })
          .attr('fill', d => {
            if (d[yField] === undefined) { return 'none'; }
            return dataPoint.fill;
          })
          .attr('stroke', d => {
            if (d[yField] === undefined) { return 'none'; }
            return dataPoint.stroke;
          });
        if (dataPoint.el === 'circle') {
          el
            .attr('cx', d => xScale(d[xField]) + centerXAxisTick)
            .attr('cy', d => d[yField] ? yScale(d[yField]) : yScale(0))
            .attr('r', 6);
          return;
        }
        if (dataPoint.el === 'rect') {
          const rectWidth = 20;
          const rectHeight = 10;
          el
            .attr('x', d => xScale(d[xField]) + centerXAxisTick - (rectWidth / 2))
            .attr('y', d => yScale(d[yField]) - (rectHeight / 2))
            .attr('width', rectWidth)
            .attr('height', rectHeight);
          return;
        }
        if (dataPoint.el === 'line') {
          const lineWidth = 10;
          const lineHeight = 5;
          el
            .attr('stroke-width', lineHeight)
            .attr('x1', d => xScale(d[xField]) + centerXAxisTick - lineWidth)
            .attr('x2', d => xScale(d[xField]) + centerXAxisTick + lineWidth)
            .attr('y1', d => yScale(d[yField]))
            .attr('y2', d => yScale(d[yField]));
          return;
        }
        if (dataPoint.el.includes('triangle')) {
          el
            .attr('d', triangle)
            .attr('transform', d => {
              const x = xScale(d[xField]) + centerXAxisTick;
              const y = yScale(d[yField]);
              return `translate(${x}, ${y})` +
                (dataPoint.el === 'triangleDown' ? 'rotate(180)' : '');
            });
        }
      }
      this.computedValue.yScale.dataPointFields.forEach(dataPointField => {
        const dataPoint = this.computedValue.dataPoint[dataPointField];
        if (Object.keys(dataPoint).length === 0) { return; }

        const classNames = [
          this.className.wrapperDataPoint,
          dataPointField.toLowerCase().replace(/[,+/<()]/g, '').split(' ').join('-')
        ];
        const circleWrapper = this.containerInfo.$chartWrapper
          .selectAll(`.${classNames.join('.')}`)
          .data([1])
          .join('g')
          .attr('class', classNames.join(' '));
        circleWrapper
          .selectAll('.data-point')
          .data(this.chartData.data, d => d.label)
          .join(
            enterDataPoint => {
              const tag = dataPoint.el.includes('triangle')
                ? 'path'
                : dataPoint.el;
              enterDataPoint
                .append(tag)
                // .attr('class', 'data-point')
                // .attr('class', (d) => {
                //   console.log('d', d);
                //   return 'data-point';
                // })
                .on('mouseover', (event, d) => {
                  let content = `<h5 class="m-0">${d[this.computedValue.xScale.field]}</h5>`;
                  this.computedValue.yScale.dataPointFields.forEach(_dataPointField => {
                    const dataPointStyle = this.computedValue.dataPoint[_dataPointField];
                    let val = this.valFormat(d[_dataPointField]);
                    if (val === 'NaN') { val = 'NA'; }
                    if (this.selectedLegend && _dataPointField !== this.selectedLegend) {
                      return;
                    }
                    content += '<div class="d-flex" style="width: 250px;">' +
                      `<span class="tooltip-bubble" style="background-color:${dataPointStyle.fill}; border: 1px solid ${dataPointStyle.stroke};"></span>` +
                      `<div><span>${_dataPointField}:</span></div>` +
                      `<span style="margin-left: auto;">${val}</span></div>`;
                  });
                  this.tooltip.tip.show(event, content);
                })
                .on('mouseout', () => {
                  this.tooltip.hide();
                })
                .call(el => renderDataPoint(el, dataPoint, this.computedValue.xScale.field, dataPointField, this.hoveringLegend, this.selectedLegend));
            },
            updatedDataPoint => {
              updatedDataPoint
                .transition()
                .duration(this.transitionDuration)
                .call(el => renderDataPoint(el, dataPoint, this.computedValue.xScale.field, dataPointField, this.hoveringLegend, this.selectedLegend));
            },
            existedDataPoint => {
              existedDataPoint.remove();
            }
          );
      });
      /* /Data Point */
    }
  }
};
</script>

<style lang="scss">
  .lollipop-chart {
    .data-point {
      // stroke: red;
      &.greyout {
        fill: none !important;
        stroke: none !important;
      }
    }
  }
</style>

<template>
  <div class="lollipop-chart container-tbd-chart">
    <p v-if="noData === true">NO DATA</p>
    <div ref="chartContainer" class="container-chart"></div>
  </div>
</template>
